import {useMessage} from '@coolcolduk/ui-mui';
import {Cloud as CloudIcon, Delete, Error as ErrorIcon} from '@mui/icons-material';
import {Button, Card, CardContent, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {env} from '../../../constants';
import useGdriveTokens, {GdriveTokenItem} from '../../../hook/useGdriveTokens';
import {GdriveTokenStatus} from '../../../constants/gdriveTokenStatus';
import {openLinkGoogleDrivePopup} from '../../../util/gdrive/linkGoogleDrive';

export default function GdriveAccounts() {
  const {completedTokens, pendingTokens, errorTokens, loading, deleteToken} = useGdriveTokens();
  const {showMessage} = useMessage();

  const handleAddAccount = () => {
    const clientId = env.gdriveOauthClientId;
    if (!clientId) {
      showMessage('Google Drive link is not configured', undefined, 'error');
      return;
    }
    const redirectUri = `${window.location.origin}/linkGoogleDriveCallback`;
    openLinkGoogleDrivePopup(redirectUri, clientId);
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        if (event.data === 'gdrive-linking' && event.origin === window.location.origin) {
          showMessage('Processing Google Drive link...', undefined, 'info');
        }
      },
      {once: true},
    );
  };

  const handleDelete = (account: GdriveTokenItem) =>
    deleteToken(account.id)
      .then(() => showMessage('Account removed', undefined, 'success'))
      .catch((e) => {
        console.error(e);
        showMessage('Failed to remove account', undefined, 'error');
      });

  if (loading) {
    return <Typography>Loading accounts...</Typography>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Google Drive accounts</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddAccount}
          disabled={!env.gdriveOauthClientId}
          startIcon={<CloudIcon />}
        >
          Add account
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {pendingTokens.map((account) => (
          <Grid size={{xs: 12, sm: 6, md: 4}} key={account.id}>
            <Card sx={{opacity: 0.7}}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={20} />
                  <Stack sx={{flexGrow: 1}}>
                    <Typography variant="subtitle1">
                      {account.status === GdriveTokenStatus.PENDING ? 'Pending...' : 'Processing...'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Linking Google account
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {errorTokens.map((account) => (
          <Grid size={{xs: 12, sm: 6, md: 4}} key={account.id}>
            <Card sx={{borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid'}}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Tooltip title={account.errorMessage ?? 'Unknown error'}>
                    <ErrorIcon color="error" />
                  </Tooltip>
                  <Stack sx={{flexGrow: 1}}>
                    <Typography variant="subtitle1" color="error">
                      Failed to link
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {account.errorMessage ?? 'Unknown error'}
                    </Typography>
                  </Stack>
                  <IconButton
                    onClick={() => handleDelete(account)}
                    color="error"
                    title="Remove"
                    aria-label="Remove"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {completedTokens.map((account) => (
          <Grid size={{xs: 12, sm: 6, md: 4}} key={account.id}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Stack sx={{flexGrow: 1}}>
                    <Typography variant="subtitle1">{account.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {account.email}
                    </Typography>
                  </Stack>
                  <IconButton
                    onClick={() => handleDelete(account)}
                    color="error"
                    title="Remove account"
                    aria-label="Remove account"
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
